import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Dots, Tick, Cross } from '@ynap/animations';
import BEMHelper from '@ynap/bem-helper';
import { Notification } from '@ynap/notification';
import Button from '../Button/Button';

const bem = new BEMHelper('AsyncButton');

class AsyncButton extends Component {
    constructor(props) {
        super(props);
        this.state = {
            animationStep: 0,
            notification: null,
            moveNotification: false,
        };
        this.setWrapperRef = this.setWrapperRef.bind(this);
        this.triggerOnClick = this.triggerOnClick.bind(this);
        this.handleRemoveError = this.handleRemoveError.bind(this);
    }

    timerIds = [];

    startPrimaryAction() {
        const { primaryAction } = this.props.asyncState;
        this.setState({ animationStep: 2 });
        const primaryTimeout = typeof primaryAction.timeout === 'undefined' ? 2000 : primaryAction.timeout;

        if (primaryTimeout) {
            const primaryTimerId = setTimeout(() => {
                this.startSecondaryAction();
            }, primaryTimeout); // tick animation step time
            this.timerIds.push(primaryTimerId);
        }
    }

    startSecondaryAction() {
        const { secondaryAction } = this.props.asyncState;

        if (secondaryAction) {
            this.setState({ animationStep: 3 });
            const secondaryTimeout = typeof secondaryAction.timeout === 'undefined' ? 2600 : secondaryAction.timeout;

            if (secondaryTimeout) {
                const secondaryTimerId = setTimeout(() => {
                    this.setState({ animationStep: 0 });
                }, secondaryTimeout); // animation secondary button step time
                this.timerIds.push(secondaryTimerId);
            }
        } else {
            this.setState({ animationStep: 0 });
        }
    }

    componentDidUpdate(prevProps) {
        const {
            asyncState,
            displayLoading = false,
            errorNotificationTimer = true,
            messages,
            notificationmessage,
            preventSkuNotification,
            sizeSkuModal,
        } = this.props;
        const { error, loading } = asyncState;
        const { error: prevError, loading: prevLoading } = prevProps.asyncState;
        const { sizeSkuModal: prevSizeSkuModal } = prevProps;

        if (!loading && (!displayLoading || (prevLoading && displayLoading))) {
            if (!asyncState.error && this.state.animationStep === 1) {
                this.startPrimaryAction();
            } else if (error && !prevError) {
                // dont clear notification if errorNotificationTimer is set to false
                const resetNotification = errorNotificationTimer ? { notification: null } : null;
                const errorTimerId = setTimeout(() => {
                    this.setState({ ...resetNotification, animationStep: 0 });
                }, 2000); // error step time
                this.timerIds.push(errorTimerId);
                this.setState({
                    notification: this.props.asyncState.error,
                    animationStep: 4,
                });
            }
        }

        if (prevSizeSkuModal !== sizeSkuModal) {
            if (this.state.animationStep === 0) {
                this.setState({ animationStep: 1, notification: null });
            } else {
                this.props.asyncState.trackError();
                this.setState({ notification: notificationmessage });

                // If NO_SIZE_SELECTED_ERROR - set hook to update sizeSelect notification
                if (notificationmessage === 'NO_SIZE_SELECTED_ERROR') {
                    this.setState({ moveNotification: !preventSkuNotification });
                    this.props.setNoSizeNotification(messages.notifications.errors({ error: notificationmessage }));
                }
            }
        }
    }

    componentDidMount() {
        const { triggerClickOnMount = false } = this.props;
        document.addEventListener('click', this.handleRemoveError);
        triggerClickOnMount && this.triggerOnClick();
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleRemoveError);
        while (this.timerIds.length) clearTimeout(this.timerIds.pop());
    }

    handleRemoveError(e) {
        const { buttonGroup, setNoSizeNotification } = this.props;
        const { moveNotification } = this.state;

        if (buttonGroup && moveNotification) {
            if (!e.target.classList.value.includes(buttonGroup)) {
                this.setState({ notification: null, moveNotification: false });
                setNoSizeNotification && setNoSizeNotification(null);
            }
        } else if (this.wrapperRef && !this.wrapperRef.contains(e.target)) {
            this.setState({ notification: null, moveNotification: false });
        }
    }

    setWrapperRef(node) {
        this.wrapperRef = node;
    }

    triggerOnClick(event) {
        const { messages, notificationmessage, preventSkuNotification, proceedaction = true, reducedMode } = this.props;

        if (reducedMode) {
            return this.props.asyncState.primaryAction.click(event);
        }

        if (proceedaction) {
            if (this.state.animationStep === 0) {
                this.props.asyncState.primaryAction.click(event);
                this.setState({ animationStep: 1, notification: null });
            } else if (this.state.animationStep === 3) {
                this.props.asyncState.secondaryAction.click(event);
            }
        } else {
            this.props.asyncState.trackError();
            this.setState({ notification: notificationmessage });

            // If NO_SIZE_SELECTED_ERROR - set hook to update sizeSelect notification
            if (notificationmessage === 'NO_SIZE_SELECTED_ERROR') {
                this.setState({ moveNotification: !preventSkuNotification });
                this.props.setNoSizeNotification(messages.notifications.errors({ error: notificationmessage }));
            }
        }
    }

    render() {
        const { animationContent = {}, arrowPosition, asyncState, className, disabled, messages = {}, notificationPosition, type } = this.props;
        let primaryButtonContent;
        let secondaryButtonContent;
        const { error, stepOne, stepThree, stepTwo } = animationContent;
        const { buttonClassName = className, notificationClassName, stackButtonClassName = null, wrapperClassName = null } = className || {};

        const { moveNotification } = this.state;

        let primaryActionButton = [buttonClassName, 'primaryButton'];
        let secondaryActionButton = [buttonClassName, 'secondaryButton'];

        if (this.state.animationStep !== 0) {
            primaryActionButton = [...primaryActionButton, 'animating'];
            secondaryActionButton = [...secondaryActionButton, 'animating'];
        }

        if (this.state.animationStep === 0) {
            primaryButtonContent = asyncState.primaryAction.message;
        } else if (this.state.animationStep === 1) {
            primaryButtonContent = stepOne || <Dots type={type} />;
        } else if (this.state.animationStep === 2) {
            primaryButtonContent = stepTwo || <Tick type={type} />;
        } else if (this.state.animationStep === 3) {
            primaryButtonContent = stepThree || <Tick type={type} />;
            secondaryButtonContent = asyncState.secondaryAction.message;

            secondaryActionButton = [...secondaryActionButton, 'fadeIn'];
            primaryActionButton = [...primaryActionButton, 'fadeOut'];
        } else if (this.state.animationStep === 4) {
            primaryButtonContent = error || <Cross type={type} />;
        }

        const notificationMessage = messages.notifications?.errors({ error: this.state.notification });
        const shouldRenderNotification = this.state.notification && messages.notifications && !moveNotification && notificationMessage;

        return (
            <div className={bem('asyncButtonWrapper', null, wrapperClassName)} ref={this.setWrapperRef}>
                <Button
                    aria-label={asyncState.primaryAction?.message}
                    className={bem(null, null, primaryActionButton)}
                    disabled={disabled}
                    onClick={this.triggerOnClick}
                    type={type}
                >
                    {primaryButtonContent}
                </Button>
                {this.props.asyncState.secondaryAction && (
                    <div className={bem('stackButtons', null, stackButtonClassName)}>
                        <Button
                            aria-label={asyncState.secondaryAction?.message}
                            className={bem(null, null, secondaryActionButton)}
                            disabled={disabled}
                            onClick={this.triggerOnClick}
                            type={type}
                        >
                            {secondaryButtonContent}
                        </Button>
                    </div>
                )}
                {shouldRenderNotification && (
                    <Notification
                        arrow={arrowPosition}
                        className={notificationClassName || bem('notification', notificationPosition)}
                        message={notificationMessage}
                        modifier="asyncButton"
                        type="error"
                    />
                )}
            </div>
        );
    }
}

AsyncButton.propTypes = {
    className: PropTypes.string,
    type: PropTypes.string.isRequired,
    proceedaction: PropTypes.bool,
    notificationmessage: PropTypes.string,
    timeout: PropTypes.number,
    asyncState: PropTypes.shape({
        loading: PropTypes.bool,
        primaryAction: PropTypes.shape({
            message: PropTypes.string,
            click: PropTypes.func,
            timeout: PropTypes.oneOf([PropTypes.number, PropTypes.bool]),
        }),
        secondaryAction: PropTypes.shape({
            message: PropTypes.string,
            click: PropTypes.func,
            timeout: PropTypes.oneOf([PropTypes.number, PropTypes.bool]),
        }),
        error: PropTypes.bool,
    }),
    messages: PropTypes.shape({
        notifications: PropTypes.shape({
            errors: PropTypes.func,
        }),
    }),
    notificationPosition: PropTypes.string,
    arrowPosition: PropTypes.string,
    triggerClickOnMount: PropTypes.bool,
    onPrimaryActionSuccess: PropTypes.func,
    animationContent: PropTypes.shape({
        stepOne: PropTypes.node,
        stepTwo: PropTypes.node,
        stepThree: PropTypes.node,
    }),
    preventSkuNotification: PropTypes.bool,
    errorNotificationTimer: PropTypes.bool,
    displayLoading: PropTypes.bool,
    sizeSkuModal: PropTypes.string,
};

AsyncButton.defaultProps = {
    notificationPosition: 'top',
    arrowPosition: 'bottom',
};

export default AsyncButton;
